/**
 * Every run this CRM has made, newest first.
 *
 * A dry run and a real one are the same shape of row and would otherwise be
 * indistinguishable a week later, so the badge is not decoration: "we imported
 * on Tuesday" is a very different sentence from "we counted on Tuesday", and
 * the whole point of keeping history is being able to tell them apart.
 *
 * A reverted run stays in the list rather than disappearing with its rows. The
 * undo is a thing that happened, and a history that hid it would leave the
 * operator looking for an import they remember making.
 */
import { History } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import type { ImportRun } from "../../../../lib/crmImport";
import { Card, CardHead, EmptyState, StatusPill, fmtNum, fmtDateTime } from "../../../ui";
import { statusLabel, statusTone } from "./labels";

/** One run's totals, summed across the entities it covered. */
function totals(run: ImportRun) {
  return run.entityStats.reduce(
    (acc, s) => ({
      read: acc.read + s.read,
      created: acc.created + s.created,
      updated: acc.updated + s.updated,
      skipped: acc.skipped + s.skipped,
    }),
    { read: 0, created: 0, updated: 0, skipped: 0 },
  );
}

/** One figure under its name. Four of these are the whole right-hand side. */
function Tally({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col items-end gap-0.5">
      <span className="crm-lab !mt-0">{label}</span>
      <span className="text-[13px] font-bold tabular-nums">{fmtNum(value)}</span>
    </div>
  );
}

export function RunHistory({ runs }: { runs: ImportRun[] }) {
  const t = useT();

  return (
    <Card>
      <CardHead title={t("crmImport.history.title")} sub={t("crmImport.history.desc")} />

      <div className="px-6 pb-6 pt-1">
        {runs.length === 0 ? (
          <EmptyState
            icon={History}
            title={t("crmImport.history.emptyTitle")}
            body={t("crmImport.history.empty")}
          />
        ) : (
          <ul className="space-y-2">
            {runs.map((run) => {
              const sum = totals(run);
              return (
                <li
                  key={run.id}
                  className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-[20px] bg-[var(--crm-sunk)] px-4 py-3"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill tone={statusTone(run.status)}>{statusLabel(t, run.status)}</StatusPill>
                      {run.dryRun ? <StatusPill tone="info">{t("crmImport.dryRunBadge")}</StatusPill> : null}
                      {run.revertedAt ? (
                        <StatusPill tone="mut">{t("crmImport.history.reverted")}</StatusPill>
                      ) : null}
                    </div>
                    <span className="text-[12px] text-muted-foreground">
                      {fmtDateTime(run.startedAt)}
                      {run.finishedAt ? ` - ${fmtDateTime(run.finishedAt)}` : ""}
                    </span>
                    {run.error ? (
                      <span className="break-words text-[12px] text-destructive">{run.error}</span>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
                    <Tally label={t("crmImport.progress.read")} value={sum.read} />
                    <Tally label={t("crmImport.progress.created")} value={sum.created} />
                    <Tally label={t("crmImport.progress.updated")} value={sum.updated} />
                    <Tally label={t("crmImport.progress.skipped")} value={sum.skipped} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
